import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { track, identifyUser, resetTelemetry } from '@/lib/telemetry';

type Profile = Tables<'profiles'>;
type Membership = Tables<'memberships'>;
type Group = Tables<'groups'>;

export type ChallengeStatus = 'scheduled' | 'active' | 'finished' | null;
export type BootState = 'initializing' | 'ready';

export function deriveChallengeStatus(group: Group | null): ChallengeStatus {
  if (!group) return null;

  const now = new Date();
  const start = new Date(group.challenge_start);
  const end = new Date(group.challenge_end);

  if (now < start) return 'scheduled';
  if (now > end) return 'finished';
  return 'active';
}

interface CompletedChallengeData {
  group: Group;
  totalDrinks: number;
  longestStreak: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  membership: Membership | null;
  group: Group | null;
  challengeStatus: ChallengeStatus;
  completedChallenge: CompletedChallengeData | null;
  loading: boolean;
  bootState: BootState;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshMembership: () => Promise<void>;
  clearCompletedChallenge: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [completedChallenge, setCompletedChallenge] =
    useState<CompletedChallengeData | null>(null);
  const [bootState, setBootState] = useState<BootState>('initializing');
  const bootStartRef = useRef<number>(
    typeof performance !== 'undefined' ? performance.now() : Date.now()
  );
  const bootTrackedRef = useRef(false);
  const signedInTrackedRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      setProfile(data ?? null);
    } catch {
      setProfile(null);
    }
  };

  const fetchMembership = async (userId: string) => {
    try {
      const { data } = await supabase
        .rpc('get_current_membership', { p_user_id: userId })
        .maybeSingle();

      if (!data) {
        setMembership(null);
        setGroup(null);
        return;
      }

      const { membership_id, group_id, role } = data;

      setMembership({
        id: membership_id,
        user_id: userId,
        group_id,
        role,
        joined_at: data.joined_at,
      } as Membership);

      const { data: groupData } = await supabase
        .from('groups')
        .select('*')
        .eq('id', group_id)
        .maybeSingle();

      setGroup(groupData ?? null);
    } catch {
      setMembership(null);
      setGroup(null);
    }
  };

  const hydrateUser = async (session: Session | null) => {
    if (!session?.user) {
      setProfile(null);
      setMembership(null);
      setGroup(null);
      setCompletedChallenge(null);
      return;
    }

    try {
      await Promise.all([
        fetchProfile(session.user.id),
        fetchMembership(session.user.id),
      ]);
    } catch {
      // swallow — boot must continue
    }
  };

  useEffect(() => {
    const { data: { subscription } } =
      supabase.auth.onAuthStateChange((event, session) => {
        // Synchronous state updates only — never await inside this callback
        setSession(session);
        setUser(session?.user ?? null);

        if (event === 'SIGNED_OUT') {
          setProfile(null);
          setMembership(null);
          setGroup(null);
          setCompletedChallenge(null);
          setBootState('ready');
          resetTelemetry();
          signedInTrackedRef.current = null;
          return;
        }

        if (session?.user) {
          identifyUser(session.user.id);
        }
        if (
          event === 'SIGNED_IN' &&
          session?.user &&
          signedInTrackedRef.current !== session.user.id
        ) {
          signedInTrackedRef.current = session.user.id;
          track('user_signed_in');
        }

        // INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED
        // Fire hydration outside the auth lock, never awaited
        void hydrateUser(session).finally(() => {
          setBootState('ready');
          if (!bootTrackedRef.current) {
            bootTrackedRef.current = true;
            const now =
              typeof performance !== 'undefined' ? performance.now() : Date.now();
            track('app_booted', {
              time_to_ready_ms: Math.round(now - bootStartRef.current),
            });
          }
        });
      });

    return () => subscription.unsubscribe();
  }, []);

  const challengeStatus = useMemo(
    () => deriveChallengeStatus(group),
    [group]
  );

  const loading = bootState !== 'ready';

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const refreshMembership = async () => {
    if (user) await fetchMembership(user.id);
  };

  const clearCompletedChallenge = () => {
    setCompletedChallenge(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        membership,
        group,
        challengeStatus,
        completedChallenge,
        loading,
        bootState,
        signOut,
        refreshProfile,
        refreshMembership,
        clearCompletedChallenge,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
